import { useAuth0 } from "@auth0/auth0-react";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import BarChart from "@mui/icons-material/BarChart";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import Newspaper from "@mui/icons-material/Newspaper";
import PermMedia from "@mui/icons-material/PermMedia";
import Send from "@mui/icons-material/Send";
import { Box, Button, Card, Skeleton, Stack, Tooltip, Typography, alpha } from "@mui/material";
import { useId } from "react";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { SeverityPill } from "../../generic/components/severity-pill";
import { getFirstName } from "../get-first-name";
import { useAssetCount } from "../hooks/use-asset-count";
import { useSentMomentCounts } from "../hooks/use-sent-moment-counts";
import { useSurveyCount } from "../hooks/use-survey-count";
import { NewInsightsCount } from "./new-insights-count";
import { PendingApprovalsCount } from "./pending-approvals-count";
import { UnderConstructionDialog } from "./under-construction-dialog";
import { UnreadInboxCount } from "./unread-inbox-count";

import type { FC, ReactNode } from "react";

const Stat: FC<{
  icon: ReactNode;
  label: string;
  value: ReactNode;
  badge?: string;
  badgeTooltip?: string;
  /** Shown in place of the value and badge, for when there is nothing to count yet. */
  emptyAction?: ReactNode;
}> = ({ icon, label, value, badge, badgeTooltip, emptyAction }) => {
  const gradientId = useId();

  return (
    <Box>
      <svg
        width="0"
        height="0"
        aria-hidden="true"
        focusable="false"
        style={{ position: "absolute" }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00B6EF" />
            <stop offset="100%" stopColor="#C24599" />
          </linearGradient>
        </defs>
      </svg>
      <Box display="flex" alignItems="center" gap={0.75} color="text.secondary">
        <Box display="flex" sx={{ "& svg": { fontSize: 16, fill: `url(#${gradientId})` } }}>
          {icon}
        </Box>
        <Typography variant="overline" lineHeight={1.5}>
          {label}
        </Typography>
      </Box>
      {emptyAction ?? (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" color="text.secondary">
            {value}
          </Typography>
          {badge && (
            <Tooltip title={badgeTooltip ?? ""}>
              <Box component="span" display="inline-flex">
                <SeverityPill
                  color="success"
                  variant="soft"
                  sx={{ textTransform: "none", gap: 0.75 }}
                >
                  <Box
                    component="span"
                    width={6}
                    height={6}
                    borderRadius="50%"
                    bgcolor="success.main"
                    flexShrink={0}
                  />
                  <Typography variant="caption" component="span">
                    {badge}
                  </Typography>
                </SeverityPill>
              </Box>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

/** What to do instead, when a stat has nothing to count. */
const EmptyStatAction: FC<{ label: string; href?: string; onClick?: () => void }> = ({
  label,
  href,
  onClick,
}) => (
  <Button
    href={href}
    onClick={onClick}
    data-analytics-id={`dashboard-empty-stat-${label.toLowerCase().replaceAll(" ", "-")}`}
    sx={{ px: 0, minWidth: 0, fontWeight: 700, "&:hover": { background: "none" } }}
  >
    {label}
  </Button>
);

const SentStat: FC = () => {
  const { data, error } = useSentMomentCounts();

  if (error) return <Stat icon={<Send />} label="Sent" value="–" />;

  return (
    <Stat
      icon={<Send />}
      label="Sent"
      value={data ? data.sent.toLocaleString() : <Skeleton width={48} />}
      badge={data ? `${data.active.toLocaleString()} active` : undefined}
      emptyAction={
        data?.sent === 0 ? (
          <EmptyStatAction label="Send a Communication" href="/moments" />
        ) : undefined
      }
    />
  );
};

const SurveysStat: FC = () => {
  const { data, error } = useSurveyCount();

  return (
    <Stat
      icon={<BarChart />}
      label="Surveys"
      value={error ? "–" : data === null ? <Skeleton width={32} /> : data.toLocaleString()}
      // TODO: don't always show New results badge
      badge="New results"
      badgeTooltip="Not live: may not be true (truth under construction)"
      emptyAction={
        data === 0 ? <EmptyStatAction label="Create a survey" href="/listen" /> : undefined
      }
    />
  );
};

const AssetsStat: FC<{ onCreateDesign: () => void }> = ({ onCreateDesign }) => {
  const { data, error } = useAssetCount();

  return (
    <Stat
      icon={<PermMedia />}
      label="Assets"
      value={error ? "–" : data === null ? <Skeleton width={32} /> : data.toLocaleString()}
      emptyAction={
        data === 0 ? (
          <EmptyStatAction label="Create a design" onClick={onCreateDesign} />
        ) : undefined
      }
    />
  );
};

const createOptions = [
  { label: "With AI", icon: <AutoAwesome />, underConstruction: true },
  { label: "Communication", icon: <Send />, href: "/moments" },
  { label: "Newsletter", icon: <Newspaper />, href: "/moments/newsletter" },
  { label: "Survey", icon: <BarChartRounded />, href: "/listen" },
];

export const DashboardHeader: FC = () => {
  const { user } = useAuth0();
  const firstName = getFirstName(user);
  const underConstructionDialog = useDialog();

  return (
    <Box
      display="grid"
      gridTemplateColumns={{ xs: "1fr", md: "1fr auto" }}
      gap={3}
      alignItems="end"
    >
      <Stack spacing={2}>
        <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
          <Typography variant="h5">Hello{firstName ? `, ${firstName}` : ""}</Typography>
          <UnreadInboxCount />
          <NewInsightsCount />
          <PendingApprovalsCount />
        </Box>
        <Card sx={{ display: "flex", gap: 4, px: 2, py: 1.5, width: "fit-content" }}>
          <SentStat />
          <SurveysStat />
          <AssetsStat onCreateDesign={() => underConstructionDialog.handleOpen()} />
        </Card>
      </Stack>
      <Box>
        <Typography variant="h6" mb={1}>
          Create:
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {createOptions.map(({ label, icon, href, underConstruction }) => (
            <Button
              key={label}
              data-analytics-id={`dashboard-create-${label.toLowerCase().replaceAll(" ", "-")}`}
              startIcon={icon}
              href={href}
              onClick={underConstruction ? () => underConstructionDialog.handleOpen() : undefined}
              sx={(theme) => {
                const { paper } = theme.palette.background;
                const tint = alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity);
                const border = "linear-gradient(135deg, #00B6EF, #C24599) border-box";
                return {
                  borderRadius: 999,
                  px: 2,
                  border: "1px solid transparent",
                  color: "text.primary",
                  background: `linear-gradient(${paper}, ${paper}) padding-box, ${border}`,
                  "&:hover": {
                    background: `linear-gradient(${tint}, ${tint}) padding-box, linear-gradient(${paper}, ${paper}) padding-box, ${border}`,
                  },
                  "& .MuiButton-startIcon": { color: "primary.main" },
                };
              }}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>
      <UnderConstructionDialog dialog={underConstructionDialog} />
    </Box>
  );
};
